<script lang="ts">
  // Side-deck window between Bo3 match games. Three zones (Main, Extra, Side); clicking a card moves
  // it to/from the Side (extra-deck monsters swap with Side via Extra, everything else via Main). A
  // 90-second deadline ticks down; the server applies whatever's committed when it fires. The MULTISET
  // of (main + extra + side) is invariant by construction (click-to-swap only), so it can't be gamed.
  import { onDestroy, onMount, untrack } from "svelte";
  import { fade, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import type { CardInfo } from "@ygoevo/yo2-data";
  import { cardsByIds } from "../../cards.ts";
  import { send } from "../../stores/net.svelte.ts";
  import DataWindow from "../ui/DataWindow.svelte";
  import NeonButton from "../ui/NeonButton.svelte";
  import { cardImage } from "../../../data.ts";
  import { draggableDialog } from "../../actions/draggableDialog.ts";

  let {
    gameNo,
    score,
    deadlineMs,
    deck,
    pickTurn = false,
  }: {
    gameNo: number;
    score: { you: number; opp: number };
    deadlineMs: number;
    deck: { main: number[]; extra: number[]; side: number[] };
    /** This player lost the previous game and also chooses who goes first next game (TCG rule). */
    pickTurn?: boolean;
  } = $props();

  // The loser's turn-order pick. Default "go first" (the advantageous, common choice).
  let goFirst = $state(true);

  // Local editable copies of the server-pushed deck pools. We seed only from the MOUNT-time value
  // (the overlay re-mounts per side phase), so untrack is the explicit Svelte 5 way to flag intent.
  let main = $state<number[]>(untrack(() => [...deck.main]));
  let extra = $state<number[]>(untrack(() => [...deck.extra]));
  let side = $state<number[]>(untrack(() => [...deck.side]));
  let cache = $state<Record<number, CardInfo>>({});
  let submitted = $state(false);

  // Lazy-load card metadata for all ids in the pool.
  onMount(async () => {
    const ids = [...new Set([...main, ...extra, ...side])];
    if (!ids.length) return;
    const cards = await cardsByIds(ids);
    cache = Object.fromEntries(cards.map((c) => [c.id, c]));
  });

  const info = (id: number) => cache[id];
  const isExtra = (id: number) => info(id)?.category === "monster-extra";

  // Counter ticks down to the deadline. Auto-submit when it hits zero (server also enforces it).
  let now = $state(Date.now());
  const tick = setInterval(() => {
    now = Date.now();
    if (now >= deadlineMs && !submitted) submit();
  }, 250);
  onDestroy(() => clearInterval(tick));
  const secondsLeft = $derived(Math.max(0, Math.ceil((deadlineMs - now) / 1000)));

  // Legality (mirrors the server's checkDeck size rules).
  const mainOk = $derived(main.length >= 40 && main.length <= 60);
  const extraOk = $derived(extra.length <= 15);
  const sideOk = $derived(side.length <= 15);
  const legal = $derived(mainOk && extraOk && sideOk);

  function moveOut(from: number[], i: number, to: number[], max: number): void {
    if (i < 0 || i >= from.length) return;
    if (to.length >= max) return;
    const id = from[i]!;
    from.splice(i, 1);
    to.push(id);
  }

  // Click a Main card: send it to Side (if Side has room). Extra-deck monsters use the Extra column
  // (which sides to/from Side too), so this is purely Main <-> Side.
  function fromMain(i: number) {
    if (submitted) return;
    moveOut(main, i, side, 15);
  }
  function fromExtra(i: number) {
    if (submitted) return;
    moveOut(extra, i, side, 15);
  }
  function fromSide(i: number) {
    if (submitted) return;
    const id = side[i];
    if (id == null) return;
    if (isExtra(id)) moveOut(side, i, extra, 15);
    else moveOut(side, i, main, 60);
  }

  function submit() {
    if (submitted) return;
    if (!legal) return;
    submitted = true;
    send({ t: "SideSubmit", main: [...main], extra: [...extra], side: [...side], ...(pickTurn ? { goFirst } : {}) });
  }
</script>

<!-- in: only, NO outros — this is a full-viewport click-catcher; Svelte 5 permanently orphans an
     interrupted outro, which would leave an invisible shield eating every duel click (see the
     duel-options backdrop note in Duel.svelte). -->
<div class="mobile-dialog-host fixed inset-0 z-50 grid place-items-center" in:fade={{ duration: 160 }}>
  <div class="modal-scrim absolute inset-0 bg-void/48"></div>

  <div use:draggableDialog role="dialog" aria-modal="true" aria-label="Side Deck" class="mobile-full-dialog relative w-[min(960px,94vw)] max-h-[90dvh]" in:scale={{ start: 0.96, opacity: 0, duration: 240, easing: expoOut }}>
    <DataWindow c={16} fill class="mobile-dialog-panel">
      <!-- header: score + timer -->
      <div data-dialog-drag-handle class="dialog-drag-handle flex shrink-0 items-center justify-between border-b border-cyan-dim/40 bg-surface-2/70 px-6 py-3 phone:px-3 phone:py-2">
        <span class="flex items-baseline gap-3">
          <span class="font-display text-[0.66rem] font-bold uppercase tracking-[0.24em] text-cyan">Side Deck</span>
          <span class="font-display text-xs tracking-[0.14em] text-ink-dim">Game {gameNo} of 3</span>
        </span>
        <span class="flex items-center gap-4">
          <span class="flex items-baseline gap-1.5 font-display tabular-nums">
            <span class="text-[0.7rem] uppercase tracking-[0.14em] text-ink-dim">Score</span>
            <span class="text-base font-bold text-cyan">{score.you}</span>
            <span class="text-ink-dim">·</span>
            <span class="text-base font-bold text-magenta">{score.opp}</span>
          </span>
          <span
            class="chamfer-btn px-3 py-1 font-display text-sm font-bold tabular-nums {secondsLeft <= 10 ? 'bg-magenta/20 text-magenta glow-magenta' : 'bg-cyan/10 text-cyan'}"
            style="--c:6px"
            title="Time until the side deck is auto-submitted"
          >{secondsLeft}s</span>
        </span>
      </div>

      <!-- body: three columns. The ONE scroll region, so the submit footer stays pinned
           (a short landscape phone otherwise pushes it under the browser UI). -->
      <div class="mobile-dialog-scroll grid gap-3 p-4 sm:grid-cols-3 phone:gap-2 phone:p-2">
        {#each [
          { key: "main", title: "Main", arr: main, click: fromMain, count: main.length, range: "40-60", ok: mainOk },
          { key: "extra", title: "Extra", arr: extra, click: fromExtra, count: extra.length, range: "0-15", ok: extraOk },
          { key: "side", title: "Side", arr: side, click: fromSide, count: side.length, range: "0-15", ok: sideOk },
        ] as zone (zone.key)}
          <div class="chamfer bg-void-2/40 p-2.5" style="--c:10px">
            <div class="flex items-baseline justify-between px-1">
              <span class="font-display text-[0.7rem] font-bold uppercase tracking-[0.18em] {zone.ok ? 'text-cyan' : 'text-magenta'}">{zone.title}</span>
              <span class="font-display text-xs tabular-nums {zone.ok ? 'text-ink-dim' : 'text-magenta'}">{zone.count} / {zone.range}</span>
            </div>
            <div class="mt-2 max-h-[52dvh] overflow-y-auto scroll-cyber pr-0.5 phone:max-h-none phone:overflow-visible">
              <div class="grid gap-1" style="grid-template-columns:repeat(auto-fill,minmax(48px,1fr))">
                {#each zone.arr as id, i (zone.key + ":" + i)}
                  <button
                    type="button"
                    onclick={() => zone.click(i)}
                    disabled={submitted}
                    title={info(id)?.name ?? "Card"}
                    class="group relative block aspect-[59/86] w-full overflow-hidden chamfer-btn transition-transform hover:-translate-y-0.5 hover:glow-cyan disabled:cursor-not-allowed disabled:opacity-60"
                    style="--c:4px"
                  >
                    <img src={cardImage(id)} alt={info(id)?.name ?? ""} draggable="false" class="size-full object-cover" />
                  </button>
                {/each}
              </div>
              {#if zone.arr.length === 0}
                <p class="grid place-items-center py-6 text-center text-[0.7rem] text-ink-dim/55">Empty</p>
              {/if}
            </div>
          </div>
        {/each}
      </div>

      <!-- footer: turn-order pick (loser only) + hint + submit -->
      <div class="shrink-0 border-t border-cyan-dim/25 bg-void-2/35 px-5 py-3 phone:px-3 phone:py-2">
        {#if pickTurn}
          <div class="mb-3 flex flex-wrap items-center gap-3">
            <span class="font-display text-[0.7rem] font-bold uppercase tracking-[0.12em] text-magenta">You lost game {gameNo - 1} · choose turn order</span>
            <div class="grid grid-cols-2 gap-1 chamfer bg-void-2/60 p-1" style="--c:7px">
              <button onclick={() => (goFirst = true)} disabled={submitted} class="chamfer-btn px-4 py-1.5 font-display text-[0.66rem] font-bold uppercase tracking-[0.1em] transition-colors disabled:opacity-60 {goFirst ? 'bg-cyan text-void glow-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:5px">Go First</button>
              <button onclick={() => (goFirst = false)} disabled={submitted} class="chamfer-btn px-4 py-1.5 font-display text-[0.66rem] font-bold uppercase tracking-[0.1em] transition-colors disabled:opacity-60 {!goFirst ? 'bg-cyan text-void glow-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:5px">Go Second</button>
            </div>
          </div>
        {/if}
        <div class="flex items-center justify-between gap-3">
          <p class="max-w-[34rem] text-xs leading-snug text-ink-dim phone:text-[0.62rem]">
            Click a card to swap it with the Side deck. Extra-deck monsters move via Extra. The next game begins as soon as both players submit.
          </p>
          <NeonButton onclick={submit} disabled={submitted || !legal}>
            {submitted ? "Submitted, waiting..." : legal ? "Submit Side Deck" : "Fix deck to submit"}
          </NeonButton>
        </div>
      </div>
    </DataWindow>
  </div>
</div>
